import { useNavigate } from "react-router-dom";
import {
  ChangePasswordApi,
  companyRegister,
  companyValidateApi,
  ForgotOTPApi,
  ForgotPasswordApi,
  logOut,
  signIn,
} from "../services/authService";
import { useState } from "react";
import { sendOTP, signUp } from "../services/authService";
import { decryptOTP } from "../utils/cryptoUtils";
import { useTimer } from "react-timer-hook";
import {
  validateEmail,
  validateOTP,
  validatePasswordChar,
  validatePassword,
  validatePhone,
  validateRequired,
} from "../utils/validators";
import { getFCMTokenAfterLogin } from "../utils/fcmHelpers";
import webSocket from "../services/onlineSocketService";
import { logoutUser } from "../utils/localStorageUtils";
import { getRegisterId } from "../utils/authId";

export const useSignin = (setDeactivatedMessage) => {
  const navigate = useNavigate();
  const [userData, setUserData] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");

  const handleInputChange = (field, value) => {
    setUserData((prev) => ({ ...prev, [field]: value }));
    if (field === "email") setEmailError("");
    if (field === "password") setPasswordError("");
  };

  const validateForm = () => {
    let isValid = true;

    if (!validateEmail(userData.email)) {
      setEmailError("Please enter a valid email address");
      isValid = false;
    }

    if (!validatePassword(userData.password)) {
      setPasswordError("Password is required");
      isValid = false;
    }

    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      const fcmToken = await getFCMTokenAfterLogin();
      const finalPayload = { ...userData, fcmToken };
      console.log(finalPayload);
      await signIn(finalPayload);
      navigate("/homepage");
    } catch (err) {
      if (err?.status === 423) {
        setDeactivatedMessage(
          err?.data || "Your account has been deactivated."
        );
        return;
      }
      if (err.data) {
        let hasFieldErrors = false;
        if (err.data.password) {
          setPasswordError(err.data.password);
          hasFieldErrors = true;
        }
        if (err.data.email) {
          setEmailError(err.data.email);
          hasFieldErrors = true;
        }
        if (!hasFieldErrors) {
          setError(err.message || "Login failed. Please try again.");
        }
      } else {
        setError(err.message || "Login failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return {
    userData,
    loading,
    error,
    emailError,
    passwordError,
    handleInputChange,
    handleSubmit,
  };
};

// Sign up
export const useRegister = ({ steps }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [stepErrors, setStepErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [generatedOtp, setGeneratedOtp] = useState("");
  const [expiryTime] = useState(() => new Date(Date.now() + 180 * 1000));
  const [canResend, setcanResend] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const expiryTimestamp = new Date();
  expiryTimestamp.setSeconds(expiryTimestamp.getSeconds() + 180);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    password: "",
    otp: "",
    emp_pic: null,
  });

  const { seconds, minutes, restart } = useTimer({
    expiryTimestamp: expiryTime,
    autoStart: false,
    onExpire: () => {
      setGeneratedOtp("");
      setStepErrors({ otp: "OTP expired. Please request a new one." });
      setcanResend(true);
    },
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === "checkbox" ? checked : value;

    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
    }));

    setStepErrors((prevErrors) => {
      const updatedErrors = { ...prevErrors };

      if (name === "email" && validateEmail(newValue)) {
        delete updatedErrors.email;
      } else if (name === "phone" && validatePhone(newValue)) {
        delete updatedErrors.phone;
      } else if (name === "password" && validatePasswordChar(newValue)) {
        delete updatedErrors.password;
      } else if (name === "otp" && validateOTP(newValue)) {
        delete updatedErrors.otp;
      }

      return updatedErrors;
    });
  };

  const handleResendOTP = async () => {
    setIsResending(true);

    try {
      const otp = await sendOTP(formData.email);
      const decryptOtp = decryptOTP(otp);
      setGeneratedOtp(decryptOtp);
      setcanResend(false);
      setStepErrors({});
      setFormData((prev) => ({
        ...prev,
        otp: "",
      }));
      restart(new Date(Date.now() + 180 * 1000), true);
    } catch (error) {
      setStepErrors(error);
    } finally {
      setIsResending(false);
    }
  };

  const validateStep = () => {
    const errors = {};
    const data = formData || {};

    switch (currentStep) {
      case 0:
        if (!validateEmail(data.email || ""))
          errors.email = "Invalid email address";
        if (!data.phone) {
          errors.phone = "Phone number is required";
        } else if (!validatePhone(data.phone)) {
          errors.phone = "Invalid phone number format. ";
        }

        if (!validatePasswordChar(data.password || "")) {
          errors.password = "Password must be at least 8 characters";
        }
        break;

      case 1:
        if (!validateOTP(data.otp || "")) errors.otp = "OTP must be 5 digits";
        break;

      case 2:
        if (!validateRequired(data.first_name || "")) {
          errors.first_name = "First name is required";
        }

        if (stepErrors?.emp_pic) {
          errors.emp_pic = stepErrors.emp_pic;
        }
        break;

      default:
        break;
    }

    setStepErrors(errors);
    return errors;
  };

  const nextStep = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    const errors = validateStep();
    if (Object.keys(errors).length > 0) {
      setIsSubmitting(false);
      return;
    }

    try {
      if (currentStep === 0) {
        const otp = await sendOTP(formData.email);
        const decryptOtp = decryptOTP(otp);
        setGeneratedOtp(decryptOtp);
        restart(new Date(Date.now() + 180 * 1000), true);
      } else if (currentStep === 1) {
        if (formData.otp !== generatedOtp) {
          setStepErrors({ otp: "Invalid OTP" });
          setIsSubmitting(false);
          return;
        }
      }

      setCurrentStep((prev) => Math.min(prev + 1, steps.length - 1));
    } catch (error) {
      setStepErrors(error.data);
    } finally {
      setIsSubmitting(false);
    }
  };

  const prevStep = () => setCurrentStep((prev) => Math.max(prev - 1, 0));

  const submit = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    const errors = validateStep();
    if (Object.keys(errors).length > 0) {
      setIsSubmitting(false);
      return;
    }

    try {
      let fcmToken = null;
      try {
        fcmToken = await getFCMTokenAfterLogin();
        console.log("Fetched FCM Token:", fcmToken);
      } catch (fcmError) {
        console.error("Failed to fetch FCM token:", fcmError);
      }

      await signUp({
        ...formData,
        role: "admin",
        fcmToken: fcmToken,
        device_name: "Web",
      });

      navigate("/company");
    } catch (error) {
      setStepErrors(error.data);
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    currentStep,
    formData,
    stepErrors,
    setStepErrors,
    isSubmitting,
    handleChange,
    isResending,
    nextStep,
    prevStep,
    submit,
    resendCooldown: { minutes, seconds },
    handleResendOTP,
    canResend: canResend,
  };
};

export const useCompany = ({ steps }) => {
  const navigate = useNavigate();

  const [currentStep, setCurrentStep] = useState(0);
  const [stepErrors, setStepErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);

  const [formData, setFormData] = useState({
    company_name: "",
    company_email: "",
    company_address: "",
    company_phone_num: "",
    city: "",
    state: "",
    pincode: "",
    company_logo: null,
  });

  const prevStep = () => setCurrentStep((prev) => Math.max(prev - 1, 0));

  const handleChange = (e) => {
    const { name, value } = e.target;
    const newValue = value;

    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
    }));

    setStepErrors((prevErrors) => {
      const updatedErrors = { ...prevErrors };

      if (name === "company_email" && validateEmail(newValue)) {
        delete updatedErrors.company_email;
      } else if (name === "company_phone_num" && validatePhone(newValue)) {
        delete updatedErrors.company_phone_num;
      } else if (validateRequired(newValue)) {
        delete updatedErrors[name];
      }

      return updatedErrors;
    });
  };

  const validateStep = () => {
    const errors = {};
    const data = formData;

    switch (currentStep) {
      case 0:
        if (!validateRequired(data.company_name)) {
          errors.company_name = "Company name is required";
        }
        if (!validateEmail(data.company_email)) {
          errors.company_email = "Invalid email address";
        }
        if (!validatePhone(data.company_phone_num)) {
          errors.company_phone_num = "Invalid phone number";
        }
        if (!data.company_logo) {
          errors.company_logo = "Company logo is required";
        }
        break;
      case 1:
        if (!validateRequired(data.company_address)) {
          errors.company_address = "Company address is required";
        }
        if (!validateRequired(data.city)) {
          errors.city = "City is required";
        }
        if (!validateRequired(data.state)) {
          errors.state = "State is required";
        }
        if (!validateRequired(data.pincode)) {
          errors.pincode = "Pincode is required";
        }
        break;

      default:
        break;
    }

    setStepErrors(errors);
    return errors;
  };

  const nextStep = async () => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    const errors = validateStep();

    if (Object.keys(errors).length > 0) {
      setIsSubmitting(false);
      return;
    }

    try {
      const data = {
        ...formData,
        role: "admin",
      };
      const response = await companyValidateApi(data);
      if (response) {
        if (currentStep < steps.length - 1) {
          setCurrentStep((prev) => prev + 1);
          setIsSubmitting(false);
        } else {
          await submit();
        }
      }
    } catch (error) {
      setStepErrors(error.data);
    } finally {
      setIsSubmitting(false);
    }
  };

  const submit = async () => {
    setIsSubmitting(true);
    setSubmitError(null);
    const errors = validateStep();
    if (Object.keys(errors).length > 0) {
      setIsSubmitting(false);
      return;
    }

    try {
      await companyRegister({
        ...formData,
        company_type: "head",
      });
      navigate("/homepage");

      setSelectedImage(null);
    } catch (error) {
      setStepErrors(error.data);
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    currentStep,
    formData,
    stepErrors,
    isSubmitting,
    submitError,
    handleChange,
    nextStep,
    prevStep,
    submit,
    setSelectedImage,
    selectedImage,
  };
};

export const useChangePassword = () => {
  const navigate = useNavigate();
  const regId = getRegisterId();

  const [formData, setFormData] = useState({
    old_password: "",
    new_password: "",
    confirm_password: "",
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({
    old_password: "",
    new_password: "",
    confirm_password: "",
    general: "",
  });

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "", general: "" }));
  };

  const validateForm = () => {
    let isValid = true;
    const newErrors = {
      old_password: "",
      new_password: "",
      confirm_password: "",
    };

    if (!formData.old_password) {
      newErrors.old_password = "Current password is required";
      isValid = false;
    }

    if (!formData.new_password) {
      newErrors.new_password = "New password is required";
      isValid = false;
    } else if (formData.new_password.length < 8) {
      newErrors.new_password = "Password must be at least 8 characters";
      isValid = false;
    } else if (!/[A-Z]/.test(formData.new_password)) {
      newErrors.new_password = "Include at least one uppercase letter";
      isValid = false;
    } else if (!/[0-9]/.test(formData.new_password)) {
      newErrors.new_password = "Include at least one number";
      isValid = false;
    } else if (!/[!@#$%^&*]/.test(formData.new_password)) {
      newErrors.new_password = "Include at least one special character";
      isValid = false;
    }

    if (formData.new_password !== formData.confirm_password) {
      newErrors.confirm_password = "Passwords do not match";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      await ChangePasswordApi({
        old_password: formData.old_password,
        new_password: formData.new_password,
        confirm_password: formData.confirm_password,
        register_id: regId,
      });

      setFormData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
      logoutUser();
      navigate("/login");
      await logOut();
      webSocket.disconnect();
    } catch (err) {
      setErrors(err.data);
    } finally {
      setLoading(false);
    }
  };

  return {
    formData,
    loading,
    errors,
    handleInputChange,
    handleSubmit,
  };
};

export const useForgotPassword = (OnClose) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [generatedOtp, setGeneratedOtp] = useState("");
  const [expiryTime] = useState(() => new Date(Date.now() + 180 * 1000));
  const [canResend, setcanResend] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const expiryTimestamp = new Date();
  expiryTimestamp.setSeconds(expiryTimestamp.getSeconds() + 180);
  const [stepErrors, setStepErrors] = useState({});

  const [errors, setErrors] = useState({
    email: "",
    new_password: "",
    confirm_password: "",
    general: "",
  });

  const [formData, setFormData] = useState({
    email: "",
    new_password: "",
    confirm_password: "",
    otp: "",
    token: "",
  });

  const { seconds, minutes, restart } = useTimer({
    expiryTimestamp: expiryTime,
    autoStart: false,
    onExpire: () => {
      setGeneratedOtp("");
      setStepErrors({ otp: "OTP expired. Please request a new one." });
      setcanResend(true);
    },
  });
  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "", general: "" }));
  };

  const handleResendOTP = async () => {
    setIsResending(true);
    try {
      const otp = await ForgotOTPApi(formData.email);
      const decryptOtp = decryptOTP(otp.otp_data.otp_code);
      setGeneratedOtp(decryptOtp);
      setcanResend(false);
      setStepErrors({});

      setFormData((prev) => ({
        ...prev,
        otp: "",
      }));
      restart(new Date(Date.now() + 180 * 1000), true);
    } catch (error) {
      setStepErrors(error);
    } finally {
      setIsResending(false);
    }
  };

  const validateEmail = () => {
    let isValid = true;
    const newErrors = { ...errors, email: "" };

    if (!formData.email) {
      newErrors.email = "Email is required";
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Invalid email address";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const validatePasswords = () => {
    let isValid = true;
    const newErrors = {
      new_password: "",
      confirm_password: "",
      general: "",
    };

    if (!formData.new_password) {
      newErrors.new_password = "New password is required";
      isValid = false;
    } else if (formData.new_password.length < 8) {
      newErrors.new_password = "Password must be at least 8 characters";
      isValid = false;
    } else if (!/[A-Z]/.test(formData.new_password)) {
      newErrors.new_password = "Include at least one uppercase letter";
      isValid = false;
    } else if (!/[0-9]/.test(formData.new_password)) {
      newErrors.new_password = "Include at least one number";
      isValid = false;
    } else if (!/[!@#$%^&*]/.test(formData.new_password)) {
      newErrors.new_password = "Include at least one special character";
      isValid = false;
    }

    if (formData.new_password !== formData.confirm_password) {
      newErrors.confirm_password = "Passwords do not match";
      isValid = false;
    }

    setErrors((prev) => ({ ...prev, ...newErrors }));
    return isValid;
  };

  const handleSubmitEmail = async () => {
    if (!validateEmail()) return;

    setLoading(true);
    try {
      const response = await ForgotOTPApi(formData.email);
      const decryptOtp = decryptOTP(response.otp_data.otp_code);
      setGeneratedOtp(decryptOtp);
      restart(new Date(Date.now() + 180 * 1000), true); // Add autoStart

      setStep(2);
    } catch (err) {
      const serverError = err?.data?.errors || "Failed to send reset email";
      setErrors((prev) => ({ ...prev, email: serverError }));
    } finally {
      setLoading(false);
    }
  };

  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
  const handleSubmitOTP = async () => {
    setLoading(true);
    try {
      console.log(formData.otp);
      if (!formData.otp) {
        setStepErrors({ otp: "Please enter the OTP." });
        return;
      }

      if (formData.otp !== generatedOtp) {
        setStepErrors({ otp: "Invalid OTP. Please try again." });
        return;
      }
      await sleep(1000);

      setStep(3);
      setStepErrors({});
    } catch (error) {
      setStepErrors({ otp: "An error occurred while verifying the OTP." });
    } finally {
      setLoading(false);
    }
  };
  const handleSubmitReset = async () => {
    if (!validatePasswords()) return;

    setLoading(true);
    try {
      await ForgotPasswordApi({
        email: formData.email,
        new_password: formData.new_password,
        confirm_password: formData.confirm_password,
      });

      if (OnClose) OnClose();
    } catch (err) {
      const errorMessage =
        err.response?.data?.message || "Password reset failed";

      if (err.response?.data?.field) {
        setErrors((prev) => ({
          ...prev,
          [err.response.data.field]: errorMessage,
        }));
      } else {
        setErrors((prev) => ({ ...prev, general: errorMessage }));
      }
    } finally {
      setLoading(false);
    }
  };

  return {
    step,
    formData,
    loading,
    errors,
    canResend,
    isResending,
    stepErrors,
    resendCooldown: { minutes, seconds },
    setStepErrors,
    handleInputChange,
    handleResendOTP,
    handleSubmitOTP,
    handleSubmitEmail,
    handleSubmitReset,
  };
};
